import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { FaX } from 'react-icons/fa6'
import Select from 'react-select'
import { z } from 'zod'

import { Button } from '../../../../components/Button'
import { Input } from '../../../../components/Input'
import { SwitchInput } from '../../../../components/SwitchInput'
import { TextArea } from '../../../../components/TextArea'
import { useDialogBox } from '../../../../hooks/useDialogBox'
import { useLocalStorage } from '../../../../hooks/useLocalStorage'
import { ProductProblem, productProblemsMap } from '../../../../interfaces/productProblemsEnum'
import { SelectOption } from '../../../../reducers/appReducer'
import { getAllProducts } from '../../../../services/productsServices'
import { createReport, Report, updateReport } from '../../../../services/reportServices'
import { getAllStoreGroups, getAllStores, getGroupCities, getGroupStates } from '../../../../services/storesServices'
import { JWT_TOKEN_KEY_NAME } from '../../../../utils/constants'

interface ModalUpsertReportProps {
  onClose: () => void
  refetchList: () => void
  report?: Report | null
}

const upsertReportFormSchema = z.object({
  store_id: z.string(),
  manager_observations: z.string().optional(),
  improvement_suggestions: z.string().optional(),
  general_observations: z.string().optional(),
  has_promoter_problem: z.boolean(),
  product_problems: z.array(
    z.object({
      id: z.string().optional(),
      problem: z.nativeEnum(ProductProblem),
      product: z.object({
        id: z.string(),
        name: z.string(),
        code: z.string().optional(),
      }),
      qty: z
        .union([z.string(), z.number().nullable(), z.undefined()])
        .transform((value) =>
          value === null || value === undefined || value === ''
            ? null
            : typeof value === 'string'
              ? parseFloat(value)
              : value,
        ),
      expire_date: z
        .string()
        .nullable()
        .optional()
        .transform((value) => {
          if (!value) return null
          const date = new Date(value)
          date.setMinutes(date.getMinutes() + date.getTimezoneOffset())
          return date.toISOString().split('T')[0]
        }),
    }),
  ),
})

type UpsertReportFormData = z.infer<typeof upsertReportFormSchema>

export function ModalUpsertReport({ onClose, refetchList, report }: ModalUpsertReportProps) {
  const [productsSelect, setProductsSelect] = useState<SelectOption[]>([])

  const [selectedGroup, setSelectedGroup] = useState<string | undefined>()
  const [selectedState, setSelectedState] = useState<string | undefined>()
  const [selectedCity, setSelectedCity] = useState<string | undefined>()

  const { showDialog } = useDialogBox()
  const [token] = useLocalStorage(JWT_TOKEN_KEY_NAME)

  const { register, control, handleSubmit, watch, setValue, formState: { errors } } = useForm<UpsertReportFormData>({
    resolver: zodResolver(upsertReportFormSchema),
    defaultValues: {
      store_id: report?.store.id,
      manager_observations: report?.manager_observations,
      improvement_suggestions: report?.improvement_suggestions,
      general_observations: report?.general_observations,
      has_promoter_problem: report?.has_promoter_problem,
      product_problems: report && report.report_product_problem.length > 0
        ? report.report_product_problem.map(item => ({
          id: item.id,
          product: item.product,
          expire_date: item.expire_date
            ? new Date(item.expire_date).toISOString().split('T')[0]
            : null,
          problem: item.problem,
          qty: item.qty,
        }))
        : [],
    },
  })

  const selectedProductProblems = watch('product_problems')

  async function handleSubmitUpsertUserForm(data: UpsertReportFormData) {
    try {
      let result
      if (report) {
        result = await updateReport({
          id: report.id,
          store_id: data.store_id,
          manager_observations: data.manager_observations,
          general_observations: data.general_observations,
          improvement_suggestions: data.improvement_suggestions,
          has_promoter_problem: data.has_promoter_problem,
          product_problems: data.product_problems,
        }, token)
      } else {
        result = await createReport({
          store_id: data.store_id,
          manager_observations: data.manager_observations,
          general_observations: data.general_observations,
          improvement_suggestions: data.improvement_suggestions,
          has_promoter_problem: data.has_promoter_problem,
          product_problems: data.product_problems,
        }, token)
      }

      if (result) {
        if (result.success) {
          showDialog({
            title: 'Sucesso',
            description: result.message,
            type: 'success',
          })
          refetchList()
          onClose()
        } else {
          showDialog({
            title: 'Erro',
            description: result.message,
            type: 'error',
          })
        }
      }
    } catch (err) {
      const errMessage = err as Error
      alert(errMessage.message)
    }
  }

  function handleAddProductProblem(data: { product: { id: string, name: string, code?: string } }) {
    setValue('product_problems', [{ ...data, problem: ProductProblem.EXPIRED, qty: 0, expire_date: null }, ...selectedProductProblems])
  }

  function handleRemoveProduct(id: string, index?: number) {
    if (selectedProductProblems) {
      const filteredList = selectedProductProblems.filter(prod => {
        const test = prod.id
          ? prod.id !== id
          : `${prod.product.id}-${prod.product.name}-${index}` !== id
        return test
      })
      setValue('product_problems', filteredList)
    }
  }

  const { data: storeGroupSelect } = useQuery({
    queryKey: ['reports_groups'],
    queryFn: async () => {
      const { storeGroups } = await getAllStoreGroups()
      return storeGroups.map((item) => ({ value: item.id, label: item.name }))
    },
  })

  const { data: statesSelect } = useQuery({
    queryKey: ['report_states', selectedGroup],
    queryFn: async () => {
      const statesList = await getGroupStates(selectedGroup)
      return statesList.map(item => ({ value: item, label: item }))
    },
  })

  const { data: citiesSelect } = useQuery({
    queryKey: ['reports_cities', selectedGroup],
    queryFn: async () => {
      if (selectedState) {
        const citiesList = (await getGroupCities(selectedGroup)).filter(item => item.state === selectedState)
        return citiesList.map(item => ({ value: item.city, label: item.city }))
      } else {
        const citiesList = await getGroupCities(selectedGroup)
        return citiesList.map(item => ({ value: item.city, label: item.city }))
      }
    },
  })

  const { data: storesSelect } = useQuery({
    queryKey: ['reports_stores', selectedCity, selectedGroup, selectedState],
    queryFn: async () => {
      const filters = {
        group: selectedGroup,
        state: selectedState,
        city: selectedCity,
      }
      const searchList = Object.entries(filters)
      const storesList = (await getAllStores()).stores
        .filter(item => {
          return searchList.every(([key, value]) => {
            if (value === undefined) return true
            if (key === 'group') {
              return item[key].id === value
            } else if (key === 'state' || key === 'city') {
              return item[key] === value
            }
            return false
          })
        })
        .map(item => ({
          id: item.id,
          name: item.name,
        }))

      return storesList.map(item => ({ value: item.id, label: item.name }))
    },
  })

  const defineSelects = useCallback(async () => {
    const [{ products }] = await Promise.all([
      getAllProducts(),
    ])
    setProductsSelect(products.map(item => ({ label: item.name, value: item.id })))
  }, [])

  useEffect(() => {
    defineSelects()
  }, [defineSelects])

  return (
    <div
      className="fixed top-0 left-0 w-screen h-screen bg-black/65 flex items-center justify-center z-[10000]"
      onClick={onClose}
    >
      <div
        className="flex flex-col bg-slate-100 relative rounded-md p-4 w-[80vw] max-h-[85dvh] md:w-96"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex justify-between mb-4">
          <h1 className="font-semibold text-2xl">
            {report
              ? 'Editar'
              : 'Criar'} relatório
          </h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <FaX />
          </Button>
        </header>
        <form
          className="flex flex-col space-y-4 w-full overflow-y-auto"
          onSubmit={handleSubmit(handleSubmitUpsertUserForm)}
        >
          <div>
            <label
              className="block text-sm/6 font-medium text-gray-900"
              htmlFor="store_group_id"
            >
              Grupo
            </label>
            <Select
              id="store_group_id"
              options={storeGroupSelect}
              onChange={state => setSelectedGroup(state?.value)}
              isDisabled={!!report}
              placeholder="Grupo..."
            />
          </div>
          <div>
            <label
              className="block text-sm/6 font-medium text-gray-900"
              htmlFor="state"
            >
              Estado (UF)
            </label>
            <Select
              id="state"
              options={statesSelect}
              onChange={state => setSelectedState(state?.value)}
              isDisabled={!!report}
              placeholder="Estado..."
            />
          </div>
          <div>
            <label
              className="block text-sm/6 font-medium text-gray-900"
              htmlFor="city"
            >
              Cidade
            </label>
            <Select
              id="city"
              options={citiesSelect}
              onChange={state => setSelectedCity(state?.value)}
              isDisabled={!!report}
              placeholder="Cidade..."
            />
          </div>
          <div>
            <Controller
              control={control}
              name="store_id"
              render={({ field: { onChange, value } }) => (
                <>
                  <label
                    className="block text-sm/6 font-medium text-gray-900 mb-1"
                    htmlFor="store_id"
                  >
                    Loja
                    <span className="text-red-500 ml-2">
                      *
                    </span>
                  </label>
                  <Select
                    id="store_id"
                    options={storesSelect}
                    onChange={state => onChange(state?.value)}
                    value={storesSelect?.filter(role => role.value === value)}
                    isDisabled={!!report}
                    placeholder="Selecione..."
                  />
                  {
                    errors.store_id?.message !== '' && (
                      <span className="text-xs font-medium text-red-500">
                        {errors.store_id?.message}
                      </span>
                    )
                  }
                </>
              )}
            />
          </div>
          {report &&
            <div>
              <Input
                placeholder="Identificador"
                label="ID"
                value={report?.id}
                readOnly
                disabled
              />
            </div>}
          <div>
            <TextArea
              label="Observações gerais"
              {...register('general_observations')}
            />
          </div>
          <div>
            <TextArea
              label="Sugestões de melhorias"
              {...register('improvement_suggestions')}
            />
          </div>
          <div>
            <TextArea
              label="Observações do gerente"
              {...register('manager_observations')}
            />
          </div>
          <div>
            <SwitchInput
              id="has_promoter_problem"
              label="Problema de promoção?"
              {...register('has_promoter_problem')}
            />
          </div>
          <div>
            <Controller
              control={control}
              name="product_problems"
              render={() => (
                <>
                  <label
                    className="block text-sm/6 font-medium text-gray-900 mb-1"
                    htmlFor="state"
                  >
                    Problema com produto
                  </label>
                  <Select
                    id="product_problems"
                    options={productsSelect}
                    onChange={state => handleAddProductProblem({ product: { id: state!.value, name: state!.label } })}
                    placeholder="Selecione..."
                  />
                </>
              )}
            />
          </div>
          <div className="">
            {selectedProductProblems && selectedProductProblems.length > 0 &&
              <>
                <strong className="block mb-3">Problemas com produtos</strong>
                {selectedProductProblems.map((item, index) => (
                  <div key={item.id || `${item.product.id}-${item.product.name}-${index}`} className="flex flex-col justify-between mb-2 p-1 border border-gray-100 bg-slate-50 rounded-md">
                    <div className="flex items-center">
                      <span className="flex-1 text-sm font-semibold">{item.product.name}</span>
                      <Button className="ml-auto" variant="ghost" size="icon" onClick={() => handleRemoveProduct(item.id || `${item.product.id}-${item.product.name}-${index}`, index)}>
                        <FaX className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="flex">
                      <div className="flex w-full items-center border border-gray-300 rounded-md overflow-hidden bg-white p-1 justify-center">
                        <Controller
                          name={`product_problems.${index}.problem`}
                          control={control}
                          render={({ field }) => (
                            <select
                              {...field}
                              id="product_problems_select"
                              value={field.value || ProductProblem.RUPTURE}
                              onChange={(e: ChangeEvent<HTMLSelectElement>) => field.onChange(e.target.value)}
                              className="h-6 flex-1 rounded-md focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                            >
                              {Object.values(ProductProblem).map(prob => (
                                <option key={prob} value={prob}>{productProblemsMap[prob]}</option>
                              ))}
                            </select>
                          )}
                        />
                        {item.problem === ProductProblem.EXPIRED &&
                          (
                            <input
                              type="date"
                              className="w-32 text-right border-l ml-1 rounded-md focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                              {...register(`product_problems.${index}.expire_date`)}
                            />
                          )}
                        <Controller
                          name={`product_problems.${index}.qty`}
                          control={control}
                          render={({ field }) => (
                            <input
                              {...field}
                              type="number"
                              className="w-10 text-right border-l ml-1 rounded-md focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                              placeholder="0"
                              min={0}
                              value={field.value || ''}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value)}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </>}
          </div>
          <footer className="ml-auto pt-4">
            <Button
              type="submit"
              size="sm"
            >
              Salvar
            </Button>
          </footer>
        </form>
      </div>
    </div>
  )
}
